/** Service Desk console: Organisations list and detail (C03/C04, SD-S1-45). */
export const serviceOrg = {
  subtitle: "The client companies you support: their people, tickets and ordering rules.",
  actions: {
    import: "Import",
    create: "New organisation",
    edit: "Edit",
    archive: "Archive",
    restore: "Restore",
    contacts: "Contacts",
  },
  stats: {
    label: "Summary",
    organisations: "Organisations",
    organisationsHint: "not archived",
    contacts: "Contacts",
    contactsHint: "across all organisations",
    pending: "Pending joiners",
    pendingHint: "waiting for approval",
  },
  filters: {
    search: "Search name, code, domain…",
    searchLabel: "Search organisations",
    kind: "Kind",
    allKinds: "All kinds",
    status: "Status",
    allStatuses: "Active and on hold",
    clear: "Clear filters",
  },
  kind: { customer: "Customer", prospect: "Prospect", partner: "Partner", internal: "Internal" },
  status: { active: "Active", on_hold: "On hold", archived: "Archived" },
  table: {
    label: "Organisations",
    organisation: "Organisation",
    tier: "Tier",
    kind: "Kind",
    contacts: "Contacts",
    status: "Status",
    manager: "Account manager",
    noTier: "No tier",
    noManager: "Unassigned",
    pending_one: "{{count}} pending",
    pending_other: "{{count}} pending",
    pendingLabel_one: "{{count}} pending joiner, review in Contacts",
    pendingLabel_other: "{{count}} pending joiners, review in Contacts",
    showing: "Showing {{shown}} of {{total}} organisations",
    loadMore: "Load more",
    contactsCount_one: "{{count}} contact",
    contactsCount_other: "{{count}} contacts",
  },
  empty: {
    title: "No organisations yet",
    body: "Add the client companies you support, or import them from a spreadsheet.",
    filteredTitle: "No organisations match",
    filteredBody: "Try another search or clear the filters.",
  },
  error: { title: "We couldn't load organisations", body: "Something went wrong on our side. Please try again." },
  form: {
    createTitle: "New organisation",
    createDescription: "A client company. Add its people and ordering rules afterwards, or import them.",
    editTitle: "Edit organisation",
    editDescription: "Changes apply to every contact and ticket of this organisation.",
    name: "Organisation name",
    namePlaceholder: "e.g. Cebu Marine Research Institute",
    code: "Code",
    codeHint: "Optional and unique. Letters, numbers, dot, dash or underscore, up to {{max}} characters. Used in lists and links.",
    industry: "Industry",
    industryPlaceholder: "e.g. Clinical laboratory",
    tier: "Tier",
    tierPlaceholder: "e.g. Gold",
    kind: "Kind",
    manager: "Account manager",
    noManager: "Not assigned",
    domain: "E-mail domain for auto-join",
    domainHint: "Optional. New joiners from this domain wait for your approval.",
    domainPlaceholder: "example.com.ph",
    notes: "Notes",
    cancel: "Cancel",
    create: "Create organisation",
    save: "Save changes",
    created: "{{name}} created",
    updated: "Organisation updated",
  },
  validation: {
    name: "Enter the organisation name ({{min}} to {{max}} characters).",
    code: "Use letters and numbers, up to {{max}} characters (dot, dash and underscore allowed inside).",
    domain: "Enter a domain such as example.com.ph (no @, spaces or paths).",
    tier: "The tier can have at most {{max}} characters.",
  },
  errors: {
    nameTaken: "An organisation with this name already exists.",
    codeTaken: "This code is already used by another organisation.",
    publicDomain: "{{domain}} is a public e-mail provider. Map a domain your client owns instead.",
    domainInUse: "{{domain}} already belongs to another organisation.",
    domainsTooMany: "An organisation can map at most {{max}} e-mail domains.",
    domainsRepeated: "This domain is already listed.",
  },
  detail: {
    sections: "Organisation sections",
    notFoundTitle: "Organisation not found",
    notFoundBody: "It may have been removed, or the link is wrong.",
    back: "Back to organisations",
    subline: "Account manager {{manager}}",
    pendingJoiners_one: "{{count}} pending joiner",
    pendingJoiners_other: "{{count}} pending joiners",
    archivedBanner: "This organisation is archived. Restore it to take new contacts and tickets.",
    archiveTitle: "Archive {{name}}?",
    archiveBody: "It leaves the lists and the contact pickers. Contacts and tickets keep their history, and you can restore it at any time.",
    archiveConfirm: "Archive organisation",
    archived: "{{name}} archived",
    restored: "{{name}} restored",
    loading: "Loading organisation",
  },
  tabs: { overview: "Overview", contacts: "Contacts", tickets: "Tickets", activity: "Activity", assets: "Assets", contracts: "Contracts", projects: "Projects", visits: "Visits", orders: "Orders" },
  overview: {
    openTickets: "Open tickets",
    openTicketsHint: "in the queue now",
    contacts: "Contacts",
    contactsHint: "people in this organisation",
    pending: "Pending joiners",
    pendingHint: "waiting for approval",
    ordering: "Ordering",
    orderingOn: "On",
    orderingOff: "Off",
    detailsTitle: "Details",
    notes: "Notes",
    noNotes: "No notes yet.",
    created: "Added",
    recentTitle: "Open tickets",
    noOpenTickets: "No open tickets.",
    noAccessTickets: "You need ticket access to see this organisation's tickets.",
  },
  fields: { kind: "Kind", tier: "Tier", industry: "Industry", manager: "Account manager", status: "Status", code: "Code" },
  domains: {
    title: "Domains & auto-join",
    intro:
      "People who verify an e-mail on one of these domains can join this organisation. Public e-mail providers such as Gmail are never accepted.",
    empty: "No domains. Joiners need an invitation.",
    list: "Mapped e-mail domains",
    add: "New domain",
    addAction: "Add domain",
    added: "@{{domain}} added",
    remove: "Remove @{{domain}}",
    removeTitle: "Remove @{{domain}}?",
    removeBody: "People with this domain will need an invitation to join. Existing contacts are not removed.",
    removeConfirm: "Remove domain",
    removed: "@{{domain}} removed",
    autoJoin: "When someone joins by domain",
    autoJoinSaved: "Auto-join updated",
    mode: {
      off: "Do not auto-join (invitation only)",
      as_user: "Let them in straight away as members",
      pending_approval: "Wait for my approval",
    },
    readOnly: "Only people who can manage organisations can change domains.",
    pendingBanner_one: "{{count}} joiner is waiting for approval.",
    pendingBanner_other: "{{count}} joiners are waiting for approval.",
    review: "Review",
  },
  ordering: {
    title: "Ordering settings",
    note: "Ordering opens up in a later release. These are the values on file; you cannot change them here yet.",
    enabled: "Ordering",
    enabledOn: "On",
    enabledOff: "Off",
    poRequired: "Purchase order number",
    poYes: "Required",
    poNo: "Not required",
    threshold: "Approval needed above",
    noThreshold: "No threshold",
    priceList: "Price list",
    paymentTerms: "Payment terms",
    notSet: "Not set",
    offBanner: "Ordering is off for this organisation.",
  },
  contactsTab: {
    label: "Contacts of this organisation",
    name: "Name",
    jobTitle: "Job title",
    role: "Org role",
    verified: "Verified",
    ordering: "Ordering",
    open: "Open",
    openLabel: "Open {{name}}",
    emptyTitle: "No contacts yet",
    emptyBody: "People join by e-mail domain, by invitation or from a spreadsheet import.",
    importContacts: "Import contacts",
    pendingBanner: "{{name}} joined with the domain {{domain}} and waits for approval.",
    review: "Review",
    showing: "Showing {{shown}} of {{total}} contacts",
    loadMore: "Load more",
    error: "We couldn't load the contacts of this organisation",
  },
  ticketsTab: {
    label: "Tickets of this organisation",
    filter: "Ticket filter",
    open: "Open",
    all: "All",
    ticket: "Ticket",
    title: "Title",
    requester: "Requester",
    status: "Status",
    priority: "Priority",
    updated: "Updated",
    emptyTitle: "No tickets",
    emptyBody: "This organisation has no tickets in this view.",
    error: "We couldn't load the tickets of this organisation",
    noAccessTitle: "Ticket access needed",
    noAccessBody: "Ask an admin for the Tickets permission to see this organisation's tickets.",
    showing: "Showing {{shown}} of {{total}} tickets",
    loadMore: "Load more",
  },
  activity: {
    title: "Activity",
    subtitle: "Tickets, people and account events, newest first",
    empty: "No activity yet.",
    created: "Organisation added",
    contactAdded: "{{name}} was added as a contact",
    contactPending: "{{name}} asked to join (domain {{domain}})",
    contactPendingNoDomain: "{{name}} asked to join",
    ticket: "{{number}} · {{title}}",
    error: "We couldn't load the activity of this organisation",
    list: "Activity of this organisation",
  },
} as const;
